import { useCallback } from 'react';
import slugg from 'slugg';
import classNames from 'classnames';

import Typography from '@components/atoms/Typography';
import { FormattedMessage } from 'utils/intl';
import { scrollTo } from 'utils/scroll';
import defer from 'utils/defer';
import useAnchorState from 'hooks/useAnchorState';
import useHash from 'hooks/useHash';

import classes from './TableOfContents.module.scss';
import messages from './TableOfContents.messages';

type Props = {
  items: string[];
  className?: string;
};

const TableOfContents = ({ className, items }: Props) => {
  const { activeItemId, setActiveItemId } = useAnchorState({
    sections: items.map((item) => slugg(item)) || [],
    offset: 32,
  });
  const setAnchor = useCallback((anchorId: string) => {
    setActiveItemId(anchorId);
    defer(() => scrollTo(`[anchor-id="${anchorId}"]`));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useHash(setAnchor);

  return (
    <div className={classNames(classes.wrapper, className)}>
      <Typography className={classes.title} variant="h2" renderAs="div">
        <FormattedMessage {...messages.tableOfContents} />
      </Typography>
      {items.map((item) => {
        const slugifiedItem = slugg(item);
        return (
          <div key={item} className={classes.item}>
            <Typography
              variant="body2"
              renderAs="div"
              className={classNames(classes.link, { [classes.bold]: activeItemId === slugifiedItem })}
              onClick={() => setAnchor(slugifiedItem)}
            >
              {item}
            </Typography>
          </div>
        );
      })}
    </div>
  );
};

export default TableOfContents;
